import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAudioSignedUrl } from '../utils/cloudApi';

// Read-only response-audio control. Audio is an artifact attached to a
// response, so this stays small and appears beside that response rather than
// introducing a second analysis surface. The signed URL is requested only
// after the author chooses to play it.
export default function AudioPlayChip({ gcsPath }) {
    const [state, setState] = useState('idle');
    const audioRef = useRef(null);
    const urlRef = useRef(null);

    const stop = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setState('idle');
    }, []);

    useEffect(() => stop, [stop]);

    const handlePlay = useCallback(async (event) => {
        event?.stopPropagation?.();
        if (!gcsPath) return;
        if (state === 'playing') {
            stop();
            return;
        }
        setState('loading');
        try {
            if (!urlRef.current) urlRef.current = await getAudioSignedUrl(gcsPath);
            const audio = new Audio(urlRef.current);
            audioRef.current = audio;
            audio.onended = () => { audioRef.current = null; setState('idle'); };
            audio.onerror = () => { audioRef.current = null; setState('error'); };
            await audio.play();
            setState('playing');
        } catch (err) {
            console.error('[AudioPlayChip] play failed:', err);
            audioRef.current = null;
            setState('error');
        }
    }, [gcsPath, state, stop]);

    const label = state === 'loading' ? 'Loading recording' : state === 'error' ? 'Recording unavailable' : state === 'playing' ? 'Pause recording' : 'Play recording';
    return (
        <button
            type="button"
            onClick={handlePlay}
            disabled={state === 'loading'}
            aria-label={label}
            title={label}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 25, height: 25, padding: 0, borderRadius: 13,
                border: state === 'error' ? '1px solid #F28B82' : '1px solid #8DBAFF',
                color: state === 'error' ? '#C62828' : '#1967D2',
                background: state === 'playing' ? '#E8F0FE' : '#FFFFFF',
                fontSize: 12, lineHeight: 1, cursor: state === 'loading' ? 'wait' : 'pointer'
            }}
        >{state === 'loading' ? '…' : state === 'error' ? '!' : state === 'playing' ? '❚❚' : '▶'}</button>
    );
}
